/**
 * Lyrics Service - Uses backend proxy with LOCAL CACHING for faster loads
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// In-memory cache for lyrics
const lyricsCache = new Map();

class LyricsService {
  /**
   * Fetch lyrics for a song via backend proxy
   * Uses caching for instant repeat loads
   * @param {string} title - Song title
   * @param {string} artist - Artist name
   * @param {number} duration - Song duration in seconds (optional)
   * @returns {Promise<Object>} Lyrics data
   */
  async fetchLyrics(title, artist, duration = null) {
    if (!title || !artist) {
      throw new Error('Song title and artist are required');
    }

    // Create cache key
    const cacheKey = `${title.toLowerCase()}_${artist.toLowerCase()}`;
    
    // Check cache first for INSTANT load
    if (lyricsCache.has(cacheKey)) {
      console.log(`[LyricsService] Cache HIT: "${title}" by "${artist}"`);
      return lyricsCache.get(cacheKey);
    }

    console.log(`[LyricsService] Fetching: "${title}" by "${artist}"`);

    // Retry logic - try up to 3 times
    const maxRetries = 3;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const params = new URLSearchParams({
          title: title,
          artist: artist,
        });
        
        if (duration) {
          params.append('duration', Math.floor(duration));
        }

        const response = await fetch(
          `${API_URL}/api/music/lyrics?${params.toString()}`,
          { 
            headers: {
              'Accept': 'application/json',
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Lyrics not found for this song');
          }
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || `Failed to fetch lyrics: ${response.status}`);
        }

        const data = await response.json();
        console.log(`[LyricsService] Got ${data.type} lyrics with ${data.lines.length} lines`);
        
        // Cache the result for instant loads next time
        lyricsCache.set(cacheKey, data);
        
        return data;
        
      } catch (err) {
        lastError = err;
        console.error(`[LyricsService] Attempt ${attempt + 1}/${maxRetries} failed:`, err.message);
        
        // Don't retry for "not found" errors
        if (err.message.includes('not found')) {
          throw err;
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
        }
      }
    }
    
    // All retries failed
    throw lastError || new Error('Failed to fetch lyrics after retries');
  }
  
  /**
   * Clear the lyrics cache
   */
  clearCache() {
    lyricsCache.clear();
  }
  
  /**
   * Prefetch lyrics for a song (call this ahead of time)
   */
  prefetchLyrics(title, artist, duration = null) {
    // Non-blocking prefetch
    this.fetchLyrics(title, artist, duration).catch(() => {});
  }
}

export default LyricsService;
